import { Portal } from '@mui/base';
import { Stack } from '@mui/joy';
import { FocusEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { NOTIFICATION_TOAST_REGION_ID } from 'notifications/constants';
import { IBellNotification, IBellNotificationCta } from 'notifications/types';
import { getVisibleNotificationToasts, isFocusLeaving } from 'notifications/utils/toast';

import { TOAST_STACK_MAX_HEIGHT, ToastStackRoot } from './style';
import { NotificationToast } from '../NotificationToast';

interface IProps {
  // The arrival cards, newest first.
  arrivals: IBellNotification[];
  onClick: (notification: IBellNotification) => void;
  onAction: (notification: IBellNotification, cta: IBellNotificationCta) => void;
  onHide: (id: number) => void;
}

// Mounted for one showing: the hold below must not outlive it, and nothing fires a mouse leave or
// a blur when the stack unmounts under the cursor.
const NotificationToastCards = ({ arrivals, onClick, onAction, onHide }: IProps) => {
  // The stack is one surface: pointing anywhere in it holds every countdown, so no card closes
  // under the cursor on its way to a control.
  const [isPointerIn, setIsPointerIn] = useState(false);
  const [isFocusIn, setIsFocusIn] = useState(false);
  const stackRef = useRef<HTMLDivElement | null>(null);

  const visible = getVisibleNotificationToasts(arrivals);
  const isHeld = isPointerIn || isFocusIn;

  const onMouseEnter = () => setIsPointerIn(true);

  const onMouseLeave = () => setIsPointerIn(false);

  // A click on a card body focuses the stack itself (tabIndex -1): only a focused control holds.
  const onFocus = (event: FocusEvent<HTMLElement>) =>
    setIsFocusIn(event.target !== event.currentTarget);

  const handleOnBlur = (event: FocusEvent<HTMLElement>) => {
    if (isFocusLeaving(event)) {
      setIsFocusIn(false);
    }
  };

  // React fires no blur when it unmounts the focused button (Close, a call to action).
  useEffect(() => {
    const stack = stackRef.current;
    const active = document.activeElement;

    setIsFocusIn((prev) => prev && !!stack && active !== stack && stack.contains(active));
  }, [arrivals]);

  // The padding gives the card shadows room inside the scroll area.
  return (
    <Stack
      ref={stackRef}
      data-testid="bellToastGroup"
      tabIndex={-1}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onFocus={onFocus}
      onBlur={handleOnBlur}
      sx={{
        gap: 1,
        maxHeight: TOAST_STACK_MAX_HEIGHT,
        overflowY: 'auto',
        margin: -1,
        padding: 1,
        pointerEvents: 'auto',
        outline: 'none',
      }}
    >
      {visible.map((notification) => (
        <NotificationToast
          key={notification.id}
          notification={notification}
          onClick={onClick}
          onAction={onAction}
          onHide={onHide}
          isHeld={isHeld}
        />
      ))}
    </Stack>
  );
};

// The toast stack of new arrivals: one Alert card each, at most four at a time. The root stays
// mounted while empty: screen readers only announce live-region content added after the region.
export const NotificationToastStack = (props: IProps) => {
  const { t } = useTranslation();

  return (
    <Portal>
      <ToastStackRoot
        id={NOTIFICATION_TOAST_REGION_ID}
        role="region"
        aria-live="polite"
        aria-label={t('labelNotifications')}
      >
        {!!props.arrivals.length && <NotificationToastCards {...props} />}
      </ToastStackRoot>
    </Portal>
  );
};
